// Facility Component
import React, { useEffect, useRef, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  TextInput,
  CustomSelect,
  FacilitySearch,
  ZipCodeInput,
  ProcedureSelect
} from '../../form-elements';
import { States, Credentials, Specialties, AgeOfPatients, Procedures, getProceduresBySpecialty } from '../formSelectOptions';

// Custom validation function for FacilityForm fields
export const getFacilityFieldValidation = (fieldName) => {
  const validationRules = {
    // Basic required fields
    facilitySearch: {
      required: { value: true, message: "Please enter a facility name." }
    },
    address: {
      required: { value: true, message: "Street address is required." }
    },
    city: {
      required: { value: true, message: "City is required." }
    },
    state: {
      required: { value: true, message: "State is required." },
    },
    zipCode: {
      required: { value: true, message: "Zip code is required." },
      pattern: {
        value: /^\d{5}$/,
        message: "Please enter a valid zip code (5 digits)."
      }
    },
    phoneNumber: {
      required: { value: true, message: "Phone number is required." },
      pattern: {
        value: /^(\d{10})$|^(\d{3}-\d{3}-\d{4})$/,
        message: "Please enter a valid phone number."
      }
    },
    facilityWebAddress: {
      pattern: {
        value: /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
        message: "Please enter a valid website URL."
      }
    },
    procedures: {
      required: { value: true, message: "Please select at least one procedure." }
    }
  };
  
  return validationRules[fieldName] || {};
};

export const FacilityForm = ({ 
  facilityId,
  removeFacility, 
  isRemovable = true,
  stateSelectRefs
}) => {
  const { register, formState: { errors }, watch } = useFormContext();
  const facilityPrefix = `facilities.${facilityId}`;
  const localStateSelectRef = useRef(null);

  // Store the ref for each facility's state select
  const setStateSelectRef = (ref) => {
    if (ref) {
      // Store ref both locally for this component and in the parent refs object
      localStateSelectRef.current = ref;
      
      // Also store in the parent component's refs object if available
      if (stateSelectRefs) {
        stateSelectRefs[facilityId] = ref;
      }
    }
  };
  
  // Log when the component mounts and unmounts to track refs
  useEffect(() => {
    
    return () => {
      // Clean up ref when component unmounts
      if (stateSelectRefs && stateSelectRefs[facilityId]) {
        delete stateSelectRefs[facilityId];
      }
    };
  }, [facilityId, stateSelectRefs]);
  
  useEffect(() => {
    // Register a custom validator for confirmedFacilities field
    register("confirmedFacilities", {
        validate: value => {
            const facilities = value || [];
            return facilities.length > 0 || "You must add at least one facility";
        }
    });
  }, [register]);

  return (
    <div>    
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <input type="hidden" id={`${facilityPrefix}.hco_entity_id`} {...register(`${facilityPrefix}.hco_entity_id`)} />

        <FacilitySearch
          label="Facility Search*" 
          placeholder="Facility Search*"
          {...register(`${facilityPrefix}.facilitySearch`, getFacilityFieldValidation('facilitySearch'))}
          aria-required="true"
          error={errors.facilities?.[facilityId]?.facilitySearch?.message}
          id={`${facilityPrefix}.facilitySearch`}
          sectionId={facilityId}
        />
        
        <TextInput  
          label="Address*"
          placeholder="Address*"
          {...register(`${facilityPrefix}.address`, getFacilityFieldValidation('address'))}
          error={errors.facilities?.[facilityId]?.address?.message}
          aria-required="true"
          id={`${facilityPrefix}.address`}
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <TextInput  
          label="City*"
          placeholder="City*"
          {...register(`${facilityPrefix}.city`, getFacilityFieldValidation('city'))}
          error={errors.facilities?.[facilityId]?.city?.message}
          aria-required="true"
          id={`${facilityPrefix}.city`}
        />

        <CustomSelect
          label="State*"
          placeholder="State"
          additionalText=" (Choose One)*"
          {...register(`${facilityPrefix}.state`, getFacilityFieldValidation('state'))}
          options={useMemo(() => Object.entries(States).map(([abbr, name]) => ({ 
            label: name, 
            value: abbr 
          })), [])}
          error={errors.facilities?.[facilityId]?.state?.message}
          aria-required="true"
          aria-label="Facility state"
          id={`${facilityPrefix}.state`}
          ref={setStateSelectRef}
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ZipCodeInput
          label="Zip Code*"
          placeholder="ZIP Code*"
          {...register(`${facilityPrefix}.zipCode`, getFacilityFieldValidation('zipCode'))}
          error={errors.facilities?.[facilityId]?.zipCode?.message}
          aria-required="true"
          id={`${facilityPrefix}.zipCode`}
        />
        <TextInput
          label="Phone Number*"
          placeholder="Phone Number*"
          {...register(`${facilityPrefix}.phoneNumber`, getFacilityFieldValidation('phoneNumber'))}
          error={errors.facilities?.[facilityId]?.phoneNumber?.message}
          aria-required="true"
          id={`${facilityPrefix}.phoneNumber`}
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        <TextInput
            label="Facility Web Address"
            placeholder="Facility Web Address"
            {...register(`${facilityPrefix}.facilityWebAddress`, getFacilityFieldValidation('facilityWebAddress'))}
            error={errors.facilities?.[facilityId]?.facilityWebAddress?.message}
            aria-required="false"
            id={`${facilityPrefix}.facilityWebAddress`}
        />
        <ProcedureSelect
          label="Add Procedures You Perform*"
          name={`${facilityPrefix}.procedures`}
          {...register(`${facilityPrefix}.procedures`, getFacilityFieldValidation('procedures'))}
          options={useMemo(() => {
            const selectedSpecialties = watch('specialties');
            const filteredProcedures = getProceduresBySpecialty(selectedSpecialties);
            return filteredProcedures;
          }, [watch('specialties')])}
          error={errors.facilities?.[facilityId]?.procedures?.message}
          required={true}
          id={`${facilityPrefix}.procedures`}
          additionalText="(Select All That Apply)"
          placeholder="Add Procedures You Perform"
				  disabled={useMemo(() => {
            const selectedSpecialties = watch('specialties');
					  return selectedSpecialties.length === 1 && selectedSpecialties?.includes('Other');
          }, [watch('specialties')])}
        />
      </div>
    </div>
  );
};